var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Quaternion, Vector3, Box3 } from "three";
import { CollisionFacet, CollisionMeshShapeType, PrimitiveShape, UTILITY_ROOM_UID, isCollisionMeshShape, isCompoundShape } from "flavr-common";
import { mainScene } from "@modules/rendering/mainScene";
import { isNonSessionRoom, modelPool } from "@modules/model";
import { CollisionRoom } from "./CollisionRoom";
import { CollisionShape } from "./CollisionShape";
import { CollisionShapeContainer } from "./CollisionShapeContainer";
import { debugTextService } from "@modules/debug";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
const EPSILON = 1e-6;
const COLLISION_DEBUG_LEGEND_UID_BASE = -2000000000;
const COLLISION_DEBUG_LEGEND_COLOR = "#ffffff";
const COLLISION_DEBUG_LEGEND_OUTLINE_COLOR = "#000000";
const COLLISION_DEBUG_LEGEND_OUTLINE_WIDTH = 0.003;
const COLLISION_DEBUG_LEGEND_FONT_SIZE = 0.04;
/**
 * Specifies if an intersection event just started or ended
 */
export var ContactType;
(function (ContactType) {
    ContactType[ContactType["ENTER"] = 0] = "ENTER";
    ContactType[ContactType["LEAVE"] = 1] = "LEAVE";
})(ContactType || (ContactType = {}));
/**
 * The CollisionWorld holds all collision shapes in all `CollisionRoom`s and is responsible to perform ray casts between
 * a ray and collision shapes and to perform intersection tests between a collision shapes. It also checks if
 * collision shapes entered or left an intersection since last frame and sends `OnEnterContactsMessage` and
 * `OnLeaveContactsMessage` via the `CollisionModule`.
 *
 * Apart from the current room, there is always a special room called the utility room which contains models that
 * persist even after room transitions.
 *
 * When doing ray casts or intersection tests and no target collisions shapes are specified then only collisions shapes
 * in the current room and utility room are considered.
 */
export class CollisionWorld {
    constructor() {
        // Setup rooms
        this.currentRoom = new CollisionRoom(); // Dummy room
        this.roomUIDToCollisionRoomMap = new Map();
        // Setup collision shapes
        this.globalCollisionShapeContainer = new CollisionShapeContainer();
        this.modelUIDToRoomMap = new Map();
        this.modelUIDToCollisionShapeMap = new Map();
        this.collisionShapeMutationVersionMap = new Map();
        // Enter and leave contacts
        this.currentlyCollidingCollisionShapeUIDs = new Map();
        this.flaggedCollisionShapeUIDs = new Set();
        // Debug
        this.showCollisionGeometry = false;
        this.hideInactiveColliders = false;
    }
    /// Getter and setter ///
    getCollisionShape(uid) {
        return this.modelUIDToCollisionShapeMap.get(uid);
    }
    getOrCreateRoom(uid) {
        let room = this.roomUIDToCollisionRoomMap.get(uid);
        if (room === undefined) {
            room = new CollisionRoom(uid);
            room.showCollisionShapes(this.showCollisionGeometry);
            this.roomUIDToCollisionRoomMap.set(uid, room);
            if (room.isUtilityRoom) {
                this.utilityRoom = room;
                this.utilityRoom.activate(mainScene, this.globalCollisionShapeContainer);
            }
        }
        return room;
    }
    getDebugSnapshot() {
        return Array
            .from(this.roomUIDToCollisionRoomMap.values())
            .map((room) => {
            const colliders = room
                .getAllCollisionShapes()
                .map((shape) => {
                var _a, _b;
                return ({
                    uid: shape.uid,
                    modelName: (_b = (_a = modelPool.getModel(shape.uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : shape.name,
                    shapeType: this.getDebugShapeType(shape.shape),
                    isSelectable: shape.isSelectable,
                    isTrigger: shape.isTrigger,
                    visible: shape.visible,
                    triCount: this.getDebugTriCount(shape.geometry),
                    position: shape.position.toArray(),
                    orientation: shape.quaternion.toArray(),
                    scale: shape.scale.toArray(),
                    geometry: shape.geometry,
                    hasBoundsTree: shape.geometry.boundsTree !== undefined,
                    isActiveForSelection: room.isShapeActiveForSelection(shape.uid)
                });
            })
                .sort((a, b) => a.modelName.localeCompare(b.modelName));
            return {
                uid: room.uid,
                roomName: room.name,
                colliderCount: colliders.length,
                isActive: room.isActive(),
                colliders
            };
        })
            .sort((a, b) => {
            if (a.isActive !== b.isActive) {
                return a.isActive ? -1 : 1;
            }
            return a.roomName.localeCompare(b.roomName);
        });
    }
    /// Room methods ///
    /**
     * Enters the room if it exists. If another room is active then this room will be left first.
     */
    enterNextRoom(roomUID) {
        if (this.currentRoom.isActive())
            this.leaveRoom(this.currentRoom.uid);
        this.currentRoom = this.getOrCreateRoom(roomUID);
    }
    /**
     * Leaves the specified room and deactivates it.
     * @param roomUID
     */
    leaveRoom(roomUID) {
        const room = this.roomUIDToCollisionRoomMap.get(roomUID);
        if (room === undefined)
            return;
        room.deactivate(mainScene, this.globalCollisionShapeContainer);
    }
    /**
     * A room needs to be activated in order to perform ray casts and intersection tests in them.
     */
    activateCurrentRoom(uid) {
        if (this.currentRoom.uid !== uid) {
            this.currentRoom = this.getOrCreateRoom(uid);
        }
        this.currentRoom.activate(mainScene, this.globalCollisionShapeContainer);
    }
    /// Collision shape methods ///
    updateOrCreateCollisionShape(uid_1, shape_1) {
        return __awaiter(this, arguments, void 0, function* (uid, shape, isSelectable = true, isTrigger = false) {
            var _a, _b, _c, _d;
            // An empty compound shape means "no collider yet" (elements are derived live from descendants and may not have
            // resolved) rather than an error - building geometry for it would throw, so just clear/skip instead.
            if (isCompoundShape(shape) && shape.length === 0) {
                this.removeCollisionShape(uid);
                return;
            }
            const mutationVersion = this.bumpCollisionShapeMutationVersion(uid);
            const sourceModel = modelPool.getModel(uid);
            if (sourceModel === undefined)
                return;
            const isCurrent = () => this.isCollisionShapeMutationCurrent(uid, mutationVersion) && modelPool.getModel(uid) === sourceModel;
            let collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
            let isNew = false;
            if (collisionShape === undefined) {
                try {
                    collisionShape = yield this.createCollisionShape(uid, shape, isSelectable, isTrigger);
                }
                catch (error) {
                    if (isCurrent()) {
                        console.error(`[collision] Failed to create collision geometry for model ${uid}`, error);
                    }
                    return;
                }
                // Collision shape might have been already deleted while we were creating it
                if (!isCurrent()) {
                    collisionShape.destroy();
                    return;
                }
                this.modelUIDToCollisionShapeMap.set(uid, collisionShape);
                isNew = true;
            }
            else if (collisionShape.shape !== shape) {
                // Build first, decide whether to keep it after - committing unconditionally would let a slower, stale build
                // that happens to finish after a newer one silently overwrite it (see buildShapeGeometry).
                let newGeometry;
                try {
                    newGeometry = yield collisionShape.buildShapeGeometry(shape);
                }
                catch (error) {
                    if (isCurrent()) {
                        console.error(`[collision] Failed to update collision geometry for model ${uid}`, error);
                    }
                    return;
                }
                if (!isCurrent()) {
                    newGeometry.dispose();
                    return;
                }
                collisionShape.commitShape(shape, newGeometry);
            }
            // Re-read the facet's current isSelectable/isTrigger rather than trusting the isSelectable/isTrigger arguments
            // above - those were captured by the caller before this function's awaits (building geometry can require an
            // async mesh/hull fetch over the bus), so something else can mutate the facet in the meantime (e.g.
            // TrainingModelStateService disabling a copyCollider child's own collider in-memory the moment its facet
            // resolves, which can race a still-in-flight initial build for that same uid). Committing the stale captured
            // value here would silently resurrect the authored state and there would be no later retry to correct it.
            const currentFacet = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(CollisionFacet);
            collisionShape.changeSelectionState((_b = currentFacet === null || currentFacet === void 0 ? void 0 : currentFacet.isSelectable) !== null && _b !== void 0 ? _b : isSelectable, (_c = currentFacet === null || currentFacet === void 0 ? void 0 : currentFacet.isTrigger) !== null && _c !== void 0 ? _c : isTrigger);
            // Update the collisionShape position
            const model = modelPool.getModel(uid);
            if (model !== undefined) {
                this.updateCollisionShapeTransform(uid, model.getWorldTransform());
            }
            // For debugging purposes
            if (this.hideInactiveColliders)
                collisionShape.visible = !this.hideInactiveColliders || collisionShape.isActive;
            // Get the room the model resides in
            const roomModel = (_d = modelPool.getModel(uid)) === null || _d === void 0 ? void 0 : _d.getRoom();
            let roomUID = roomModel === null || roomModel === void 0 ? void 0 : roomModel.getUID();
            if (roomUID === undefined || (roomModel === null || roomModel === void 0 ? void 0 : roomModel.isRoot()) === true || isNonSessionRoom(roomModel)) {
                if (this.isModelDetached(uid)) {
                    roomUID = UTILITY_ROOM_UID;
                }
                else if (isNonSessionRoom(roomModel)) {
                    roomUID = UTILITY_ROOM_UID;
                }
                else {
                    roomUID = this.currentRoom.uid;
                }
            }
            const collisionRoom = this.getOrCreateRoom(roomUID);
            // Remember the room for the respective collision shape
            const oldCollisionRoom = this.modelUIDToRoomMap.get(uid);
            const roomChanged = oldCollisionRoom !== undefined && oldCollisionRoom !== collisionRoom;
            // Add or update collision shape in room - a room change needs a real addCollisionShape (roomNode + container),
            // not just updateCollisionShape, which only touches the container's active/trigger bookkeeping and never adds
            // the shape to a room it wasn't already part of.
            if (isNew || roomChanged)
                collisionRoom.addCollisionShape(collisionShape);
            else
                collisionRoom.updateCollisionShape(collisionShape);
            if (oldCollisionRoom === undefined) {
                this.modelUIDToRoomMap.set(uid, collisionRoom);
            }
            else if (roomChanged) {
                // Detach (not remove+destroy) from the old room - the shape is moving to a different room, not being deleted,
                // so its geometry/bounds tree must survive. Destroying it here used to silently break both raycasting
                // (CollisionRoom checks for a bounds tree and skips shapes without one) and the in-world debug outline, while
                // the shape's own isSelectable flag and geometry attribute data (unaffected by GPU-side disposal) still read
                // back fine everywhere else - making this very hard to notice.
                oldCollisionRoom.detachCollisionShape(collisionShape);
                this.modelUIDToRoomMap.set(uid, collisionRoom);
            }
            this.flaggedCollisionShapeUIDs.add(uid);
        });
    }
    updateCollisionShapeSelectableState(uid, isSelectable, isTrigger) {
        const collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
        if (collisionShape === undefined)
            return;
        const room = this.modelUIDToRoomMap.get(uid);
        if (room === undefined) {
            console.warn('[selection] activateCollisionShape: Room is undefined');
            return;
        }
        collisionShape.changeSelectionState(isSelectable, isTrigger !== null && isTrigger !== void 0 ? isTrigger : collisionShape.isTrigger);
        room.updateCollisionShape(collisionShape);
        // For debugging purposes
        if (this.hideInactiveColliders)
            collisionShape.visible = !this.hideInactiveColliders || collisionShape.isActive;
        if (isSelectable) {
            this.flaggedCollisionShapeUIDs.add(uid);
        }
        else {
            if (this.flaggedCollisionShapeUIDs.has(uid))
                this.flaggedCollisionShapeUIDs.delete(uid);
        }
    }
    refreshCollisionShapeRoom(uid) {
        const collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
        if (collisionShape === undefined)
            return;
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const roomModel = model.getRoom();
        let roomUID = roomModel === null || roomModel === void 0 ? void 0 : roomModel.getUID();
        if (roomUID === undefined || (roomModel === null || roomModel === void 0 ? void 0 : roomModel.isRoot()) === true || isNonSessionRoom(roomModel))
            roomUID = (this.isModelDetached(uid) || isNonSessionRoom(roomModel)) ? UTILITY_ROOM_UID : this.currentRoom.uid;
        const collisionRoom = this.getOrCreateRoom(roomUID);
        const previousRoom = this.modelUIDToRoomMap.get(uid);
        if (previousRoom === collisionRoom)
            return;
        if (previousRoom !== undefined)
            previousRoom.removeCollisionShape(collisionShape);
        collisionRoom.addCollisionShape(collisionShape);
        this.modelUIDToRoomMap.set(uid, collisionRoom);
        this.flaggedCollisionShapeUIDs.add(uid);
    }
    updateCollisionShapeTransform(uid, worldTransform) {
        const collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
        if (collisionShape === undefined)
            return;
        collisionShape.matrix.copy(collisionShape.localTransform);
        collisionShape.matrixWorld.copy(worldTransform).multiply(collisionShape.localTransform);
        collisionShape.matrixWorld.decompose(collisionShape.position, collisionShape.quaternion, collisionShape.scale);
        collisionShape.updateMatrix();
        collisionShape.updateMatrixWorld(true);
        this.flaggedCollisionShapeUIDs.add(uid);
    }
    updateCollisionShapeLocalTransform(uid, localTransform) {
        const collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
        if (collisionShape === undefined) {
            return;
        }
        // Ensure that scale is not zero or close to zero
        if (localTransform.elements[0] * localTransform.elements[5] * localTransform.elements[10] < EPSILON) {
            const position = new Vector3();
            const orientation = new Quaternion();
            const scale = new Vector3();
            localTransform.decompose(position, orientation, scale);
            scale.x = this.ensureVectorComponentNotZero(scale.x);
            scale.y = this.ensureVectorComponentNotZero(scale.y);
            scale.z = this.ensureVectorComponentNotZero(scale.z);
            // Ensure orientation is not broken
            if (isNaN(orientation.x) || isNaN(orientation.y) || isNaN(orientation.z) || isNaN(orientation.w)) {
                orientation.identity();
            }
            localTransform.compose(position, orientation, scale);
        }
        collisionShape.localTransform.copy(localTransform);
        // Also make sure to update world transform of the collision shape
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        this.updateCollisionShapeTransform(model.getUID(), model.getWorldTransform());
    }
    removeCollisionShape(uid) {
        this.bumpCollisionShapeMutationVersion(uid);
        const room = this.modelUIDToRoomMap.get(uid);
        if (room === undefined) {
            return;
        }
        const collisionShape = this.modelUIDToCollisionShapeMap.get(uid);
        if (collisionShape === undefined) {
            return;
        }
        this.modelUIDToCollisionShapeMap.delete(uid);
        this.modelUIDToRoomMap.delete(uid);
        room.removeCollisionShape(collisionShape);
        this.removeEmptyRoom(room);
    }
    removeAllCollisionShapes() {
        for (const uid of this.collisionShapeMutationVersionMap.keys()) {
            this.bumpCollisionShapeMutationVersion(uid);
        }
        for (const room of this.modelUIDToRoomMap.values()) {
            room.removeAllCollisionShapes();
        }
        this.modelUIDToRoomMap.clear();
        this.modelUIDToCollisionShapeMap.clear();
    }
    /// Intersection and collision methods ///
    performRaycast(origin, direction, targetCollisionShapeUIDs, backfaceCulling, includeTriggers) {
        return this.currentRoom.performRaycast(origin, direction, targetCollisionShapeUIDs, backfaceCulling, includeTriggers);
    }
    /**
     * Performs an intersection test between the test shape and multiple target shapes and returns all intersections
     * with the target shapes.
     *
     * If `targetCollisionShapeUIDs` is not provided then all active collision shapes (excluding the test shape) in
     * either the current room and the utility room are used to test for intersection. If `includeTriggers` is set to
     * true, then also trigger collisions shapes are used (note this only applies if no `targetCollisionShapeUIDs` were
     * provided).
     *
     * By default, no hitpoints are computed, to also compute hitpoints set `computeHitpoint` to true (note: this is slow
     * and should only be used if hitpoints are needed)
     *
     * @param testCollisionShape the collision shape we want to test against
     * @param targetCollisionShapeUIDs the collisions shapes that might or might not intersect the test shape, default: undefined
     * @param computeHitpoint if true hitpoints are computed, default: false
     * @param includeTriggers if true trigger collision shapes are included in the targetCollisionShapeUIDs, default: false
     */
    performIntersection(testCollisionShape, targetCollisionShapeUIDs, computeHitpoint, includeTriggers) {
        return this.currentRoom.performIntersection(testCollisionShape, targetCollisionShapeUIDs, computeHitpoint, includeTriggers);
    }
    /**
     * Checks if collision shapes entered or left an intersection since the last frame. This method should be called
     * every frame.
     */
    updateCollisionContacts() {
        // Perform collision checks for all objects that changed since last time
        const allIntersections = [];
        const testedContactPairs = new Set();
        for (const flaggedCollisionShapeUID of this.flaggedCollisionShapeUIDs) {
            const collisionShape = this.modelUIDToCollisionShapeMap.get(flaggedCollisionShapeUID);
            if (collisionShape === undefined) {
                // Collision shape might have been deleted since the last update
                continue;
            }
            // We only want to check collisions for active or trigger collision shapes
            if (!collisionShape.isTrigger && !collisionShape.isSelectable)
                continue;
            // We also want to collide with objects that are triggers
            const result = this.currentRoom.performIntersection(collisionShape, undefined, false, true, testedContactPairs);
            for (const target of result) {
                allIntersections.push({ sourceUID: flaggedCollisionShapeUID, targetUID: target.uid });
            }
        }
        // Go through all collisions and check whether we have new contacts
        const changedContacts = [];
        const checkedCollisions = new Set();
        for (const intersection of allIntersections) {
            const collisionShapeAUID = intersection.sourceUID;
            const collisionShapeBUID = intersection.targetUID;
            // We don't want duplicate collision contacts
            const contactID = this.createContactID(collisionShapeAUID, collisionShapeBUID);
            if (checkedCollisions.has(contactID))
                continue;
            // Add both directions, so we only need to test one direction
            const contactIDReverse = this.createContactID(collisionShapeBUID, collisionShapeAUID);
            checkedCollisions.add(contactID);
            checkedCollisions.add(contactIDReverse);
            if (!(this.currentlyCollidingCollisionShapeUIDs.has(contactID)
                || this.currentlyCollidingCollisionShapeUIDs.has(contactIDReverse))) {
                changedContacts.push({ collisionShapeAUID, collisionShapeBUID, contactType: ContactType.ENTER });
            }
            this.currentlyCollidingCollisionShapeUIDs.set(contactID, [collisionShapeAUID, collisionShapeBUID]);
        }
        // Check if some contacts left
        for (const [contactID, [collisionShapeAUID, collisionShapeBUID]] of this.currentlyCollidingCollisionShapeUIDs.entries()) {
            if (checkedCollisions.has(contactID))
                continue;
            // We only check the collisions for flagged collision shapes
            if (!(this.flaggedCollisionShapeUIDs.has(collisionShapeAUID)
                || this.flaggedCollisionShapeUIDs.has(collisionShapeBUID)))
                continue;
            changedContacts.push({ collisionShapeAUID, collisionShapeBUID, contactType: ContactType.LEAVE });
            const contactIDReverse = this.createContactID(collisionShapeBUID, collisionShapeAUID);
            this.currentlyCollidingCollisionShapeUIDs.delete(contactID);
            this.currentlyCollidingCollisionShapeUIDs.delete(contactIDReverse);
        }
        // Remove flagged collisions as we already processed them
        this.flaggedCollisionShapeUIDs.clear();
        return changedContacts;
    }
    /// Debugging ///
    toggleCollisionShapesVisibility() {
        this.showCollisionGeometry = !this.showCollisionGeometry;
        this.updateDebugVisualization();
    }
    toggleHideInactiveColliders() {
        this.hideInactiveColliders = !this.hideInactiveColliders;
        this.updateDebugVisualization();
    }
    /// Private collision shape methods ///
    createCollisionShape(uid_1, shape_1) {
        return __awaiter(this, arguments, void 0, function* (uid, shape, isSelectable = true, isTrigger = false) {
            const collisionShape = new CollisionShape();
            yield collisionShape.create(uid, shape, isSelectable, isTrigger);
            collisionShape.setDebugOutlineEnabled(this.showCollisionGeometry);
            return collisionShape;
        });
    }
    /// Private room helper methods ///
    removeEmptyRoom(room) {
        // We can't remove a room that we are currently in
        if (room === this.currentRoom)
            return;
        // Don't remove the utility room
        if (room === this.utilityRoom)
            return;
        // Only remove empty rooms
        if (!room.isEmpty())
            return;
        this.roomUIDToCollisionRoomMap.delete(room.uid);
    }
    ensureVectorComponentNotZero(value, threshold = EPSILON) {
        const sign = (value >= 0.0) ? 1 : -1;
        const absValue = Math.abs(value);
        return (absValue > threshold) ? value : sign * Math.max(absValue, threshold);
    }
    isModelDetached(uid) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return false;
        const room = model.getRoom();
        return room === undefined || room.isRoot();
    }
    getDebugShapeType(shape) {
        var _a;
        if (isCompoundShape(shape))
            return "Compound";
        if (isCollisionMeshShape(shape))
            return (shape.shape === CollisionMeshShapeType.HULL) ? "Hull" : "Mesh";
        return (_a = PrimitiveShape[shape]) !== null && _a !== void 0 ? _a : String(shape);
    }
    getDebugTriCount(geometry) {
        var _a, _b, _c;
        const indexCount = (_a = geometry.index) === null || _a === void 0 ? void 0 : _a.count;
        if (indexCount !== undefined)
            return indexCount / 3;
        return ((_c = (_b = geometry.getAttribute("position")) === null || _b === void 0 ? void 0 : _b.count) !== null && _c !== void 0 ? _c : 0) / 3;
    }
    /// Private enter leave contacts method ///
    createContactID(uidA, uidB) {
        return `${uidA}_${uidB}`;
    }
    /// Mutation versioning ///
    bumpCollisionShapeMutationVersion(uid) {
        var _a;
        const nextVersion = ((_a = this.collisionShapeMutationVersionMap.get(uid)) !== null && _a !== void 0 ? _a : 0) + 1;
        this.collisionShapeMutationVersionMap.set(uid, nextVersion);
        return nextVersion;
    }
    isCollisionShapeMutationCurrent(uid, mutationVersion) {
        return this.collisionShapeMutationVersionMap.get(uid) === mutationVersion;
    }
    /// Private debugging methods ///
    updateDebugVisualization() {
        this.currentRoom.showCollisionShapes(this.showCollisionGeometry);
        if (this.utilityRoom !== undefined)
            this.utilityRoom.showCollisionShapes(this.showCollisionGeometry);
        Array
            .from(this.modelUIDToCollisionShapeMap.values())
            .forEach(shape => {
            shape.setDebugOutlineEnabled(this.showCollisionGeometry);
            shape.visible = !this.hideInactiveColliders || shape.isActive;
        });
        if (this.showCollisionGeometry) {
            this.createLabels();
        }
        else {
            this.removeLabels();
        }
    }
    createLabels() {
        const getDebugTextTargets = () => {
            var _a, _b;
            const debugTextTargets = [];
            const rooms = (this.utilityRoom !== undefined)
                ? [this.currentRoom, this.utilityRoom]
                : [this.currentRoom];
            for (const room of rooms) {
                for (const collisionShape of room.getAllCollisionShapes()) {
                    if (!collisionShape.visible)
                        continue;
                    collisionShape.updateMatrixWorld(true);
                    const worldPosition = new Vector3();
                    collisionShape.getWorldPosition(worldPosition);
                    const worldBounds = this.computeWorldBounds(collisionShape, worldPosition);
                    const worldCenter = worldBounds.getCenter(new Vector3());
                    const worldAnchor = new Vector3(worldCenter.x, worldBounds.max.y + 0.2, worldCenter.z);
                    debugTextTargets.push({
                        name: (_b = (_a = modelPool.getModel(collisionShape.uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : collisionShape.name,
                        uid: collisionShape.uid,
                        box: worldBounds,
                        position: worldPosition,
                        centerPosition: worldCenter,
                        anchorPosition: worldAnchor,
                        orientation: collisionShape.quaternion,
                        color: this.getCollisionDebugColor(collisionShape.isSelectable, collisionShape.isTrigger)
                    });
                }
            }
            debugTextTargets.push(...this.getCollisionDebugLegendTargets());
            return debugTextTargets;
        };
        debugTextService.showDebugText("CollisionModule", getDebugTextTargets);
    }
    removeLabels() {
        debugTextService.hideDebugText("CollisionModule");
    }
    computeWorldBounds(collisionShape, fallbackPosition) {
        const geometry = collisionShape.geometry;
        if (geometry.boundingBox === null)
            geometry.computeBoundingBox();
        const localBounds = geometry.boundingBox;
        if (localBounds !== null) {
            const worldBounds = localBounds.clone().applyMatrix4(collisionShape.matrixWorld);
            const hasFiniteBounds = Number.isFinite(worldBounds.min.x) &&
                Number.isFinite(worldBounds.min.y) &&
                Number.isFinite(worldBounds.min.z) &&
                Number.isFinite(worldBounds.max.x) &&
                Number.isFinite(worldBounds.max.y) &&
                Number.isFinite(worldBounds.max.z);
            if (hasFiniteBounds)
                return worldBounds;
        }
        const radius = 0.05;
        return new Box3(fallbackPosition.clone().addScalar(-radius), fallbackPosition.clone().addScalar(radius));
    }
    getCollisionDebugColor(isSelectable, isTrigger) {
        if (isSelectable)
            return "red";
        if (isTrigger)
            return "yellow";
        return "blue";
    }
    getCollisionDebugLegendTargets() {
        const cameraWorldPosition = localPlayerContainer.cameraWorldPosition.clone();
        const cameraDirection = localPlayerContainer.cameraWorldViewDirection.clone().normalize();
        const cameraUp = localPlayerContainer.cameraWorldUpDirection.clone().normalize();
        const cameraRight = new Vector3().crossVectors(cameraDirection, cameraUp).normalize();
        const legendAnchor = cameraWorldPosition
            .clone()
            .add(cameraDirection.clone().multiplyScalar(1.25))
            .add(cameraRight.clone().multiplyScalar(-0.3))
            .add(cameraUp.clone().multiplyScalar(0.2));
        const makeLegendTarget = (uidOffset, name, color, verticalOffset, horizontalOffset = 0) => {
            const position = legendAnchor
                .clone()
                .add(cameraUp.clone().multiplyScalar(verticalOffset))
                .add(cameraRight.clone().multiplyScalar(horizontalOffset));
            const legendBoundsRadius = 0.06;
            return {
                name,
                uid: COLLISION_DEBUG_LEGEND_UID_BASE + uidOffset,
                box: new Box3(position.clone().addScalar(-legendBoundsRadius), position.clone().addScalar(legendBoundsRadius)),
                position,
                centerPosition: position.clone(),
                anchorPosition: position.clone(),
                orientation: new Quaternion(),
                color,
                outlineColor: COLLISION_DEBUG_LEGEND_OUTLINE_COLOR,
                outlineWidth: COLLISION_DEBUG_LEGEND_OUTLINE_WIDTH,
                fontSize: COLLISION_DEBUG_LEGEND_FONT_SIZE,
                lockLabelPosition: true
            };
        };
        return [
            makeLegendTarget(0, "----------------------------", COLLISION_DEBUG_LEGEND_COLOR, 0.05),
            makeLegendTarget(1, "Collision colors:", COLLISION_DEBUG_LEGEND_COLOR, 0),
            makeLegendTarget(2, "red = selectable", "red", -0.07),
            makeLegendTarget(3, "yellow = trigger", "yellow", -0.14),
            makeLegendTarget(4, "blue = inactive", "blue", -0.21),
            makeLegendTarget(5, "----------------------------", COLLISION_DEBUG_LEGEND_COLOR, -0.27)
        ];
    }
}
